import type { Category, PlanningCycle, QuickNote, SpaceTemplateBundle, TaskTemplate, Todo } from "../../domain/entities.ts";
import { normalizeReminders } from "../tasks/reminders.ts";
import { randomUuid } from "../../infrastructure/crypto/random.ts";
import { validDailyDate } from "../quick-notes/daily-notes.ts";
import { parseReferenceHistory, type ReferenceRevision } from "../notes/reference-history.ts";
import { parseContentHistory, type ContentRevision } from "../notes/content-history.ts";
import { portableFields } from "../custom-fields/portable.ts";

export const EXPORT_FORMAT = "weirdo-export";
export const BACKUP_FORMAT = "weirdo-encrypted-backup";
export const FORMAT_VERSION = 2;

export interface PortableSpace {
  contentHistory?: Record<string, ContentRevision[]>;
  id: string;
  name: string;
  color: string;
  categories: Category[];
  tasks: Todo[];
  attachments?: PortableAttachment[];
  templates?: TaskTemplate[];
  spaceTemplates?: SpaceTemplateBundle[];
  epics?: import("../../domain/entities.ts").Epic[];
  cycles?: PlanningCycle[];
  referenceHistory?: Record<string, ReferenceRevision[]>;
}

export interface PortableAttachment {
  taskId: string;
  fileName: string;
  contentType: string;
  data: string;
}

export interface PortableArchive {
  format: typeof EXPORT_FORMAT;
  version: typeof FORMAT_VERSION;
  exportedAt: string;
  spaces: PortableSpace[];
  savedViews: unknown[];
  personalTemplates: TaskTemplate[];
  quickNotes: PortableQuickNote[];
}
export interface PortableQuickNote extends QuickNote { contentHistory?: ContentRevision[] }

interface EncryptedBackup {
  format: typeof BACKUP_FORMAT;
  version: number;
  encryption: { algorithm: "AES-GCM"; derivation: "PBKDF2-SHA-256"; iterations: number; salt: string; iv: string };
  ciphertext: string;
}

export function createArchive(spaces: PortableSpace[], savedViews: unknown[] = [], personalTemplates: TaskTemplate[] = [], quickNotes: QuickNote[] = []): PortableArchive {
  return { format: EXPORT_FORMAT, version: FORMAT_VERSION, exportedAt: new Date().toISOString(), spaces, savedViews, personalTemplates, quickNotes };
}

export function parseArchive(value: unknown): PortableArchive {
  if (!value || typeof value !== "object") throw new Error("The selected file is not a WeirDo export.");
  const archive = value as Partial<PortableArchive>;
  if (archive.format !== EXPORT_FORMAT) throw new Error("The selected file is not a WeirDo export.");
  if (![1, FORMAT_VERSION].includes(Number(archive.version))) throw new Error(`Export version ${String(archive.version)} is not supported.`);
  if (!Array.isArray(archive.spaces)) throw new Error("The export does not contain a spaces list.");
  return {
    format: EXPORT_FORMAT,
    version: FORMAT_VERSION,
    exportedAt: typeof archive.exportedAt === "string" ? archive.exportedAt : new Date(0).toISOString(),
    spaces: archive.spaces.map(parseSpace),
    savedViews: Array.isArray(archive.savedViews) ? archive.savedViews : [],
    personalTemplates: Array.isArray(archive.personalTemplates) ? archive.personalTemplates.filter(isTemplate).map(item => ({ ...structuredClone(item), ...portableFields(item as unknown as Record<string, unknown>) })) : [],
    quickNotes: Array.isArray(archive.quickNotes) ? archive.quickNotes.filter(isQuickNote).map(normalizeQuickNote) : [],
  };
}

function isQuickNote(value: unknown): value is QuickNote {
  return !!value && typeof value === "object" && typeof (value as QuickNote).body === "string";
}

function normalizeQuickNote(note: PortableQuickNote): PortableQuickNote {
  const now = Date.now();
  return { ...(note.contentHistory ? { contentHistory: parseContentHistory(note.contentHistory, "quick_note") } : {}), ...(validDailyDate(note.dailyDate) ? { dailyDate: note.dailyDate } : {}), id: typeof note.id === "string" ? note.id : "", rootId: "", body: note.body.slice(0, 100_000), pinned: !!note.pinned, archivedAt: typeof note.archivedAt === "number" ? note.archivedAt : null, convertedTaskId: null, deleted: false, createdAt: Number.isFinite(note.createdAt) ? note.createdAt : now, updatedAt: Number.isFinite(note.updatedAt) ? note.updatedAt : now };
}

function parseSpace(value: unknown): PortableSpace {
  if (!value || typeof value !== "object") throw new Error("The export contains an invalid space.");
  const space = value as Partial<PortableSpace>;
  if (typeof space.name !== "string" || !space.name.trim()) throw new Error("An exported space has no name.");
  return {
    id: typeof space.id === "string" ? space.id : "",
    name: space.name.trim().slice(0, 120),
    contentHistory: space.contentHistory && typeof space.contentHistory === "object" && !Array.isArray(space.contentHistory) ? Object.fromEntries(Object.entries(space.contentHistory).filter(([id]) => space.tasks?.some(task => task.id === id && !task.deleted)).map(([id, revisions]) => [id, parseContentHistory(revisions, "todo")])) : {},
    color: typeof space.color === "string" ? space.color : "indigo",
    categories: Array.isArray(space.categories) ? space.categories.filter(isCategory).map(normalizeCategory) : [],
    tasks: Array.isArray(space.tasks) ? space.tasks.filter(isTodo).map(normalizeTodo) : [],
    attachments: Array.isArray(space.attachments) ? space.attachments.filter(isAttachment).map(item => structuredClone(item)) : [],
    templates: Array.isArray(space.templates) ? space.templates.filter(isTemplate).map(item => ({ ...structuredClone(item), ...portableFields(item as unknown as Record<string, unknown>) })) : [],
    spaceTemplates: Array.isArray(space.spaceTemplates) ? space.spaceTemplates.filter(isSpaceTemplate).map(item => structuredClone(item)) : [],
    epics: Array.isArray(space.epics) ? space.epics.filter(item => item && typeof item.id === "string" && typeof item.name === "string" && !!item.name.trim() && !item.deleted).map(item => ({ ...structuredClone(item), name: item.name.trim().slice(0,120), description: typeof item.description === "string" ? item.description.slice(0,25000) : "", archivedAt: typeof item.archivedAt === "number" ? item.archivedAt : null })) : [],
    cycles: Array.isArray(space.cycles) ? space.cycles.filter(isCycle).map(item => structuredClone(item)) : [],
    referenceHistory: space.referenceHistory && typeof space.referenceHistory === "object" && !Array.isArray(space.referenceHistory) ? Object.fromEntries(Object.entries(space.referenceHistory).filter(([id]) => Array.isArray(space.categories) && space.categories.some(category => category.id === id && !category.deleted)).map(([id, revisions]) => [id, parseReferenceHistory(revisions)])) : {},
  };
}

function isCategory(value: unknown): value is Category {
  return !!value && typeof value === "object" && typeof (value as Category).name === "string";
}

function boundedReferenceNote(value: unknown): string {
  if (typeof value !== "string") return "";
  const encoder = new TextEncoder();
  if (encoder.encode(value).length <= 65_536) return value;
  const characters = Array.from(value);
  let low = 0;
  let high = Math.min(characters.length, 65_536);
  while (low < high) {
    const middle = Math.ceil((low + high) / 2);
    if (encoder.encode(characters.slice(0, middle).join("")).length <= 65_536) low = middle;
    else high = middle - 1;
  }
  return characters.slice(0, low).join("");
}

function normalizeCategory(category: Category): Category {
  const now = Date.now();
  return {
    ...structuredClone(category),
    ...portableFields(category as unknown as Record<string, unknown>),
    id: typeof category.id === "string" ? category.id : "",
    rootId: typeof category.rootId === "string" ? category.rootId : "",
    name: category.name.trim().slice(0, 120),
    referenceNote: boundedReferenceNote(category.referenceNote),
    referenceAnchors: Array.isArray(category.referenceAnchors) ? category.referenceAnchors.filter(anchor => anchor && typeof anchor.id === "string" && !!anchor.id && typeof anchor.title === "string").slice(0, 200).map(anchor => ({ id: anchor.id.slice(0, 100), title: anchor.title.slice(0, 200) })) : [],
    viewType: category.viewType === "board" ? "board" : category.viewType === "timeline" ? "timeline" : category.viewType === "table" ? "table" : "list",
    boardColumns: Array.isArray(category.boardColumns) ? category.boardColumns.filter(column => typeof column === "string") : [],
    sortBy: ["manual", "title", "created", "due", "priority", "status"].includes(category.sortBy || "") ? category.sortBy : "manual",
    sortDirection: category.sortDirection === "desc" ? "desc" : "asc",
    pageSize: typeof category.pageSize === "number" && category.pageSize > 0 ? category.pageSize : 25,
    showCompleted: !!category.showCompleted,
    deleted: !!category.deleted,
    createdAt: Number.isFinite(category.createdAt) ? category.createdAt : now,
    updatedAt: Number.isFinite(category.updatedAt) ? category.updatedAt : now,
  };
}

function isTodo(value: unknown): value is Todo {
  return !!value && typeof value === "object" && typeof (value as Todo).title === "string";
}

function isAttachment(value: unknown): value is PortableAttachment {
  if (!value || typeof value !== "object") return false;
  const item = value as PortableAttachment;
  return typeof item.taskId === "string" && typeof item.fileName === "string" && typeof item.contentType === "string" && typeof item.data === "string";
}

function isTemplate(value: unknown): value is TaskTemplate {
  return !!value && typeof value === "object" && typeof (value as TaskTemplate).name === "string" && typeof (value as TaskTemplate).title === "string";
}

function isSpaceTemplate(value: unknown): value is SpaceTemplateBundle {
  return !!value && typeof value === "object" && (value as SpaceTemplateBundle).schemaVersion === 1 && typeof (value as SpaceTemplateBundle).name === "string" && Number.isInteger((value as SpaceTemplateBundle).version) && (value as SpaceTemplateBundle).version > 0 && (value as SpaceTemplateBundle).version <= 1000 && typeof (value as SpaceTemplateBundle).familyId === "string" && Array.isArray((value as SpaceTemplateBundle).collections) && Array.isArray((value as SpaceTemplateBundle).taskTemplates);
}

function isCycle(value: unknown): value is PlanningCycle {
  return !!value && typeof value === "object" && typeof (value as PlanningCycle).name === "string" && ["weekly", "biweekly"].includes((value as PlanningCycle).cadence);
}

export function normalizeTodo(todo: Todo): Todo {
  const now = Date.now();
  const priority = ["none", "low", "medium", "high", "urgent"].includes(todo.priority) ? todo.priority : "none";
  const type = todo.type === "checklist" ? "checklist" : "text";
  const tagOrder = Array.isArray(todo.tagOrder) ? todo.tagOrder.filter(tag => typeof tag === "string") : Object.keys(todo.tags || {}).filter(tag => todo.tags[tag]);
  return {
    ...structuredClone(todo),
    ...portableFields(todo as unknown as Record<string, unknown>),
    id: typeof todo.id === "string" ? todo.id : "",
    rootId: typeof todo.rootId === "string" ? todo.rootId : "",
    title: todo.title.slice(0, 240),
    description: typeof todo.description === "string" ? todo.description : "",
    type,
    subtasks: Array.isArray(todo.subtasks) ? todo.subtasks.filter(item => item && typeof item.title === "string").map(item => ({ id: item.id || randomUuid(), title: item.title.slice(0, 240), note: typeof item.note === "string" ? item.note.slice(0, 2000) : "", completed: !!item.completed })) : [],
    position: Number.isFinite(todo.position) ? todo.position : now,
    pinned: !!todo.pinned,
    ...(todo.checkpoint !== undefined ? { checkpoint: !!todo.checkpoint } : {}),
    completed: !!todo.completed,
    completedAt: typeof todo.completedAt === "number" ? todo.completedAt : null,
    priority,
    dueDate: typeof todo.dueDate === "string" ? todo.dueDate : null,
    startDate: typeof todo.startDate === "string" ? todo.startDate : null,
    startAt: typeof todo.startAt === "string" ? todo.startAt : null,
    endAt: typeof todo.endAt === "string" ? todo.endAt : null,
    scheduleMode: todo.scheduleMode === "floating" ? "floating" : "fixed",
    scheduleTimeZone: todo.scheduleMode === "floating" && typeof todo.scheduleTimeZone === "string" ? todo.scheduleTimeZone : null,
    wallStartAt: todo.scheduleMode === "floating" && typeof todo.wallStartAt === "string" ? todo.wallStartAt : null,
    wallEndAt: todo.scheduleMode === "floating" && typeof todo.wallEndAt === "string" ? todo.wallEndAt : null,
    estimatedMinutes: typeof todo.estimatedMinutes === "number" && todo.estimatedMinutes > 0 ? todo.estimatedMinutes : null,
    reminderAt: typeof todo.reminderAt === "string" ? todo.reminderAt : null,
    reminders: normalizeReminders(todo),
    recurrence: todo.recurrence || null,
    comments: Array.isArray(todo.comments) ? todo.comments : [],
    categoryId: typeof todo.categoryId === "string" ? todo.categoryId : null,
    assignedTo: Array.isArray(todo.assignedTo) ? todo.assignedTo.filter(id => typeof id === "string") : [],
    tags: Object.fromEntries(tagOrder.map(tag => [tag, true])),
    tagOrder,
    externalCalendars: Array.isArray(todo.externalCalendars) ? todo.externalCalendars.filter(provider => provider === "google" || provider === "microsoft") : [],
    archivedAt: typeof todo.archivedAt === "number" ? todo.archivedAt : null,
    deleted: !!todo.deleted,
    createdAt: Number.isFinite(todo.createdAt) ? todo.createdAt : now,
    updatedAt: Number.isFinite(todo.updatedAt) ? todo.updatedAt : now,
  };
}

export function tasksToCsv(tasks: Todo[], categoryName: (id: string) => string, epicName: (id: string) => string = () => ""): string {
  const header = ["title", "description", "status", "priority", "due_date", "collection", "tags", "task_type", "subtasks", "epic"];
  const rows = tasks.filter(task => !task.deleted).map(task => [
    task.title,
    task.description,
    task.completed ? "completed" : "open",
    task.priority,
    task.dueDate || "",
    task.categoryId ? categoryName(task.categoryId) : "",
    (task.tagOrder || Object.keys(task.tags || {}).filter(tag => task.tags[tag])).join("|"),
    task.type,
    (task.subtasks || []).map(item => `${item.completed ? "[x]" : "[ ]"} ${item.title}`).join(" | "),
    task.epicId ? epicName(task.epicId) : "",
  ]);
  return [header, ...rows].map(row => row.map(csvCell).join(",")).join("\r\n");
}

export function csvToSpace(contents: string, name = "Imported tasks"): PortableSpace {
  const rows = parseCsvRows(contents);
  if (rows.length < 2) throw new Error("The CSV file contains no tasks.");
  const headers = rows[0].map(value => value.trim().toLowerCase().replaceAll(" ", "_"));
  const column = (...names: string[]) => names.map(value => headers.indexOf(value)).find(index => index >= 0) ?? -1;
  const titleColumn = column("title", "task", "name", "content");
  if (titleColumn < 0) throw new Error("The CSV file needs a title, task, name, or content column.");
  const descriptionColumn = column("description", "notes", "note");
  const statusColumn = column("status", "completed", "done");
  const priorityColumn = column("priority");
  const dueColumn = column("due_date", "due", "deadline");
  const categoryColumn = column("collection", "subcategory", "project", "list");
  const tagsColumn = column("tags", "labels");
  const epicColumn = column("epic", "goal");
  const epicIds = new Map<string, string>();
  const categoryIds = new Map<string, string>();
  const now = Date.now();
  const tasks = rows.slice(1).filter(row => row.some(Boolean)).map((row, position) => {
    const epicName = epicColumn >= 0 ? (row[epicColumn] || "").trim().slice(0,120) : "";
    if (epicName && !epicIds.has(epicName)) epicIds.set(epicName, randomUuid());
    const categoryName = categoryColumn >= 0 ? (row[categoryColumn] || "").trim() : "";
    if (categoryName && !categoryIds.has(categoryName)) categoryIds.set(categoryName, randomUuid());
    const status = statusColumn >= 0 ? (row[statusColumn] || "").toLowerCase() : "";
    const priorityValue = priorityColumn >= 0 ? (row[priorityColumn] || "").toLowerCase() : "";
    const priority = ["low", "medium", "high", "urgent"].includes(priorityValue) ? priorityValue as Todo["priority"] : "none";
    const tags = tagsColumn >= 0 ? (row[tagsColumn] || "").split(/[|,;]/).map(tag => tag.trim().toLowerCase()).filter(Boolean) : [];
    const completed = ["completed", "complete", "done", "true", "yes", "1", "x"].includes(status);
    return normalizeTodo({
      id: randomUuid(), rootId: "", title: (row[titleColumn] || "").trim(), description: descriptionColumn >= 0 ? row[descriptionColumn] || "" : "",
      type: "text", subtasks: [], position: now + position, completed, completedAt: completed ? now : null, priority,
      dueDate: dueColumn >= 0 && row[dueColumn] ? row[dueColumn].slice(0, 10) : null, categoryId: categoryName ? categoryIds.get(categoryName)! : null,
      epicId: epicName ? epicIds.get(epicName)! : null, assignedTo: [], tags: Object.fromEntries(tags.map(tag => [tag, true])), tagOrder: tags, deleted: false, createdAt: now, updatedAt: now,
    });
  }).filter(task => task.title);
  if (!tasks.length) throw new Error("The CSV file contains no titled tasks.");
  const categories = [...categoryIds].map(([categoryName, id]) => ({ id, rootId: "", name: categoryName, viewType: "list" as const, boardColumns: [], deleted: false, createdAt: now, updatedAt: now }));
  return { id: "", name, color: "indigo", categories, tasks, epics: [...epicIds].map(([name,id]) => ({ id, rootId: "", name, description: "", archivedAt: null, deleted: false, createdAt: now, updatedAt: now })), attachments: [], templates: [], cycles: [] };
}

function parseCsvRows(contents: string): string[][] {
  const rows: string[][] = [];
  let row: string[] = [];
  let cell = "";
  let quoted = false;
  for (let index = 0; index < contents.length; index++) {
    const character = contents[index];
    if (character === "\"" && quoted && contents[index + 1] === "\"") { cell += "\""; index++; }
    else if (character === "\"") quoted = !quoted;
    else if (character === "," && !quoted) { row.push(cell); cell = ""; }
    else if ((character === "\n" || character === "\r") && !quoted) {
      if (character === "\r" && contents[index + 1] === "\n") index++;
      row.push(cell); rows.push(row); row = []; cell = "";
    } else cell += character;
  }
  if (cell || row.length) { row.push(cell); rows.push(row); }
  return rows;
}

function csvCell(value: string): string {
  return /[",\r\n]/.test(value) ? `"${value.replaceAll("\"", "\"\"")}"` : value;
}

export async function encryptArchive(archive: PortableArchive, password: string): Promise<string> {
  if (password.length < 10) throw new Error("Use a backup password with at least 10 characters.");
  const salt = crypto.getRandomValues(new Uint8Array(16));
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const iterations = 250_000;
  const key = await deriveKey(password, salt, iterations, ["encrypt"]);
  const plaintext = new TextEncoder().encode(JSON.stringify(archive));
  const ciphertext = new Uint8Array(await crypto.subtle.encrypt({ name: "AES-GCM", iv }, key, plaintext));
  const backup: EncryptedBackup = {
    format: BACKUP_FORMAT,
    version: FORMAT_VERSION,
    encryption: { algorithm: "AES-GCM", derivation: "PBKDF2-SHA-256", iterations, salt: base64(salt), iv: base64(iv) },
    ciphertext: base64(ciphertext),
  };
  return JSON.stringify(backup, null, 2);
}

export async function decryptArchive(contents: string, password: string): Promise<PortableArchive> {
  const backup = JSON.parse(contents) as EncryptedBackup;
  if (backup.format !== BACKUP_FORMAT || ![1, FORMAT_VERSION].includes(backup.version) || backup.encryption?.algorithm !== "AES-GCM") throw new Error("The selected file is not a supported encrypted backup.");
  try {
    const salt = unbase64(backup.encryption.salt);
    const iv = unbase64(backup.encryption.iv);
    const key = await deriveKey(password, salt, backup.encryption.iterations, ["decrypt"]);
    const plaintext = await crypto.subtle.decrypt({ name: "AES-GCM", iv: iv as BufferSource }, key, unbase64(backup.ciphertext) as BufferSource);
    return parseArchive(JSON.parse(new TextDecoder().decode(plaintext)));
  } catch {
    throw new Error("The backup password is incorrect or the file is damaged.");
  }
}

async function deriveKey(password: string, salt: Uint8Array, iterations: number, usages: KeyUsage[]): Promise<CryptoKey> {
  const material = await crypto.subtle.importKey("raw", new TextEncoder().encode(password), "PBKDF2", false, ["deriveKey"]);
  return crypto.subtle.deriveKey({ name: "PBKDF2", salt: salt as BufferSource, iterations, hash: "SHA-256" }, material, { name: "AES-GCM", length: 256 }, false, usages);
}

function base64(bytes: Uint8Array): string {
  let binary = "";
  bytes.forEach(byte => binary += String.fromCharCode(byte));
  return btoa(binary);
}

function unbase64(value: string): Uint8Array {
  return Uint8Array.from(atob(value), character => character.charCodeAt(0));
}
